import { UserGroup } from '@provision/entity-types';
import { gql, Reference, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { useNotifications } from 'src/utils/hooks';
import { useGroupRules, UserGroupRules } from './group-rules';

const MANAGE_USER_PERMISSIONS_MUTATION_MUTATION = gql`
  mutation ManageUserPermissions($data: ManageUserPermissionsDataInput!) {
    manageUserPermissions(params: $data) {
      userId
    }
  }
`;

export type ManageUserToUserGroupParams = {
  userId: string;
  userGroups: Partial<UserGroup>[];
};

export type ManageUserPermissions = {
  userId: string;
};

export const useManageUserPermissions = () => {
  const { showErrorAlert } = useNotifications();

  const [manageUserPermissionsMutation] = useMutation<{
    manageUserPermissions: ManageUserPermissions;
  }>(MANAGE_USER_PERMISSIONS_MUTATION_MUTATION, {
    onError: (error) => {
      console.error(error);
      const message = 'Something went wrong managing user permissions, please try again';
      showErrorAlert(message);
    },
  });

  const { groupRules } = useGroupRules();

  const manageUserPermissions = useCallback(
    async (params: ManageUserToUserGroupParams) => {
      if (!params.userGroups || !params.userId) {
        return;
      }

      const createdAt = new Date().toISOString();

      const optimisticResponseData: ManageUserPermissions = {
        userId: params.userId,
      };

      const { data } = await manageUserPermissionsMutation({
        variables: {
          data: {
            userGroupIds: params.userGroups.map((item) => item.id),
            userId: params.userId,
          },
        },
        optimisticResponse: {
          manageUserPermissions: optimisticResponseData,
        },
        update: (cache, { data: response }) => {
          const userId = response?.manageUserPermissions.userId;
          if (!userId) return;

          const id = cache.identify({
            __typename: 'PlatformUser',
            id: userId,
          });

          cache.modify({
            id,
            fields: {
              userGroups(existingItems = [], { toReference, readField }) {
                const existingUserGroups = existingItems as Reference[];

                const groupsNotAllowedToModify = groupRules[
                  UserGroupRules.AccessToUpdatePrivateGroups
                ]
                  ? []
                  : existingUserGroups.filter((ref) => !readField('isPublic', ref));

                const updatedUserGroups = params.userGroups?.map((item) => {
                  const existingGroup = existingUserGroups?.find(
                    (ref) => item.id === readField('id', ref),
                  );

                  if (existingGroup) {
                    return existingGroup;
                  }

                  const newGroup = {
                    id: item?.id!,
                    userGroupId: item?.id!,
                    name: item?.name!,
                    description: '',
                    createdAt,
                    isPublic: item.isPublic,
                    ruleValues: [],
                    platformUsers: [],
                    updatedAt: createdAt,
                    __typename: 'UserGroup',
                  };

                  return toReference(newGroup);
                });

                return [...groupsNotAllowedToModify, ...updatedUserGroups];
              },
            },
          });
        },
      });

      return data?.manageUserPermissions;
    },
    [manageUserPermissionsMutation, groupRules],
  );

  return {
    manageUserPermissions,
  };
};
